import type { NachaParseResult } from '@/types/nacha';
import jsPDF from 'jspdf';

/**
 * Mask account number (show last 4 digits)
 */
export function maskAccount(account: string): string {
  if (account.length <= 4) return account;
  return '*'.repeat(account.length - 4) + account.slice(-4);
}

/**
 * Mask routing number (show last 4 digits)
 */
export function maskRouting(routing: string): string {
  if (routing.length <= 4) return routing;
  return '*'.repeat(routing.length - 4) + routing.slice(-4);
}

/**
 * Format amount (cents to dollars)
 */
export function formatAmount(amountCents: string | number): string {
  const cents = typeof amountCents === 'string' ? parseInt(amountCents) : amountCents;
  return (cents / 100).toFixed(2);
}

/**
 * Format date YYMMDD -> MM/DD/YYYY
 */
export function formatDate(date: string): string {
  if (date.length !== 6) return date;
  const year = parseInt(date.substring(0, 2));
  const month = date.substring(2, 4);
  const day = date.substring(4, 6);
  const fullYear = year >= 50 ? 1900 + year : 2000 + year;
  return `${month}/${day}/${fullYear}`;
}

/**
 * Format date and time to MM/DD/YYYY hh:mm:ss AM/PM
 * @param date - Date string in YYMMDD format
 * @param time - Time string in HHmm format (24-hour)
 * @returns Formatted date and time string
 */
export function formatDateTime(date: string, time: string): string {
  // Format date part
  if (date.length !== 6) return `${date} ${time}`;
  const year = parseInt(date.substring(0, 2));
  const month = date.substring(2, 4);
  const day = date.substring(4, 6);
  const fullYear = year >= 50 ? 1900 + year : 2000 + year;
  
  // Format time part (HHmm -> hh:mm:ss AM/PM)
  if (time.length !== 4) return `${month}/${day}/${fullYear} ${time}`;
  const hours24 = parseInt(time.substring(0, 2));
  const minutes = time.substring(2, 4);
  
  // Convert to 12-hour format with AM/PM
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
  const formattedHours = hours12.toString().padStart(2, '0');
  
  return `${month}/${day}/${fullYear} ${formattedHours}:${minutes}:00 ${period}`;
}

/**
 * Export as JSON
 */
export function exportToJson(result: NachaParseResult): string {
  return JSON.stringify(result, null, 2);
}

/**
 * Export as CSV
 */
export function exportToCsv(result: NachaParseResult): string {
  if (!result.file) return '';
  
  const rows: string[][] = [
    [
      'Batch Number',
      'Company Name',
      'SEC Code',
      'Entry Description',
      'Effective Date',
      'Transaction Code',
      'Routing Number',
      'Account Number',
      'Amount',
      'Individual Name',
      'Individual ID',
      'Trace Number',
      'Addenda Count',
    ],
  ];
  
  result.file.batches.forEach((batch) => {
    batch.entries.forEach((entry) => {
      rows.push([
        batch.header.batchNumber,
        batch.header.companyName,
        batch.header.standardEntryClassCode,
        batch.header.companyEntryDescription,
        formatDate(batch.header.effectiveEntryDate),
        entry.transactionCode,
        entry.receivingDFI,
        entry.dfaAccountNumber,
        formatAmount(entry.amount),
        entry.individualName,
        entry.individualIdNumber,
        entry.traceNumber,
        entry.addenda ? entry.addenda.length.toString() : '0',
      ]);
    });
  });
  
  // Format cells: use ="value" for numeric fields to force text format in Excel
  return rows.map((row, rowIndex) => 
    row.map((cell, colIndex) => {
      // For Routing Number (col 6), Account Number (col 7), and Trace Number (col 11)
      // use Excel formula format to preserve as text
      if (rowIndex > 0 && (colIndex === 6 || colIndex === 7 || colIndex === 11)) {
        return `"=""${cell}"""`;
      }
      return `"${cell}"`;
    }).join(',')
  ).join('\n');
}

/**
 * Download file
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get transaction type label
 */
export function getTransactionTypeLabel(code: string): string {
  const codeNum = parseInt(code);
  switch (codeNum) {
    case 21: return 'Return/NOC Debit (Checking)';
    case 22: return 'Credit (Checking)';
    case 23: return 'Prenote Credit (Checking)';
    case 24: return 'Zero Dollar Credit (Checking)';
    case 26: return 'Return Debit (Checking)';
    case 27: return 'Debit (Checking)';
    case 28: return 'Prenote Debit (Checking)';
    case 29: return 'Zero Dollar Debit (Checking)';
    case 31: return 'Return/NOC Credit (Savings)';
    case 32: return 'Credit (Savings)';
    case 33: return 'Prenote Credit (Savings)';
    case 34: return 'Zero Dollar Credit (Savings)';
    case 36: return 'Return Debit (Savings)';
    case 37: return 'Debit (Savings)';
    case 38: return 'Prenote Debit (Savings)';
    case 39: return 'Zero Dollar Debit (Savings)';
    default: return `Unknown (${code})`;
  }
}

/**
 * Get service class label
 */
export function getServiceClassLabel(code: string): string {
  switch (code) {
    case '200': return 'Mixed Debits and Credits';
    case '220': return 'Credits Only';
    case '225': return 'Debits Only';
    default: return `Unknown (${code})`;
  }
}

/**
 * Get account type from transaction code
 */
function getAccountType(code: string): string {
  const codeNum = parseInt(code);
  if ([22, 23, 24, 27, 28, 29].includes(codeNum)) return 'Checking';
  if ([32, 33, 34, 37, 38, 39].includes(codeNum)) return 'Savings';
  return 'Unknown';
}

/**
 * Get ACH type (Debit/Credit) from transaction code
 */
function getAchType(code: string): string {
  const codeNum = parseInt(code);
  if ([22, 23, 24, 32, 33, 34].includes(codeNum)) return 'Credit';
  if ([27, 28, 29, 37, 38, 39].includes(codeNum)) return 'Debit';
  return 'Unknown';
}

/**
 * Export as PDF
 */
export function exportToPdf(result: NachaParseResult): void {
  if (!result.file) return;
  
  const doc = new jsPDF();
  const file = result.file;
  let yPos = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const lineHeight = 7;
  const contentWidth = pageWidth - 2 * margin;
  
  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace: number = 20) => {
    if (yPos + requiredSpace > pageHeight - margin - 15) {
      doc.addPage();
      yPos = 20;
      return true;
    }
    return false;
  };
  
  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ACH Transactions Report', margin, yPos);
  
  // Date
  const reportDate = new Date().toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${reportDate}`, pageWidth - margin, yPos, { align: 'right' });
  
  yPos += 12;
  
  // Process each batch (company)
  file.batches.forEach((batch, batchIndex) => {
    if (batchIndex > 0) {
      yPos += 5; // Add spacing between batches
    }
    checkNewPage(50);
    
    // Batch separator line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    // Company header (without "Company:" prefix)
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(batch.header.companyName, margin, yPos);
    yPos += lineHeight;
    
    // Transfer type and date
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const transferType = batch.header.companyEntryDescription || 'Transfer';
    const effectiveDate = formatDate(batch.header.effectiveEntryDate);
    doc.text(`Type: ${transferType} | Effective Date: ${effectiveDate}`, margin, yPos);
    yPos += lineHeight + 3;
    
    // Batch summary
    let batchDebit = 0;
    let batchCredit = 0;
    
    batch.entries.forEach((entry) => {
      const achType = getAchType(entry.transactionCode);
      const amount = parseFloat(formatAmount(entry.amount));
      if (achType === 'Debit') {
        batchDebit += amount;
      } else if (achType === 'Credit') {
        batchCredit += amount;
      }
    });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Batch Totals: ${batch.entries.length} Transactions | Debit: $${batchDebit.toFixed(2)} | Credit: $${batchCredit.toFixed(2)}`, margin, yPos);
    yPos += lineHeight + 5;
    
    // Table header with background
    checkNewPage(30);
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 5, contentWidth, 8, 'F');
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    
    // Simplified column layout
    doc.text('Name', margin + 2, yPos);
    doc.text('Bank Routing', margin + 50, yPos);
    doc.text('Bank Account', margin + 80, yPos);
    doc.text('Type', margin + 115, yPos);
    doc.text('Debit', margin + 135, yPos);
    doc.text('Credit', margin + 160, yPos);
    
    yPos += lineHeight + 2;
    
    // Entries
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    
    batch.entries.forEach((entry) => {
      checkNewPage(8);
      
      const achType = getAchType(entry.transactionCode);
      const amount = parseFloat(formatAmount(entry.amount));
      const accountType = getAccountType(entry.transactionCode);
      
      // Name (truncate if too long)
      let name = entry.individualName.trim();
      if (name.length > 30) name = name.substring(0, 27) + '...';
      doc.text(name, margin + 2, yPos);
      
      // Bank Transit
      doc.text(entry.receivingDFI, margin + 50, yPos);
      
      // Bank Account
      doc.text(entry.dfaAccountNumber, margin + 80, yPos);
      
      // Type
      doc.text(`${achType.substring(0, 1)}/${accountType.substring(0, 1)}`, margin + 115, yPos);
      
      // Amounts
      if (achType === 'Debit') {
        doc.text(`$${amount.toFixed(2)}`, margin + 135, yPos);
        doc.text('-', margin + 160, yPos);
      } else {
        doc.text('-', margin + 135, yPos);
        doc.text(`$${amount.toFixed(2)}`, margin + 160, yPos);
      }
      
      yPos += lineHeight;
    });
    
    yPos += 3;
  });
  
  // Report totals - start on a new page
  doc.addPage();
  yPos = 20;
  
  // Separator line
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Report Totals', margin, yPos);
  yPos += lineHeight + 5;
  
  const totalEntries = file.batches.reduce((sum, batch) => sum + batch.entries.length, 0);
  const totalDebit = file.batches.reduce((sum, batch) => sum + parseFloat(formatAmount(batch.control.totalDebitAmount)), 0);
  const totalCredit = file.batches.reduce((sum, batch) => sum + parseFloat(formatAmount(batch.control.totalCreditAmount)), 0);
  const netAmount = totalCredit - totalDebit;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0); // Reset text color to black
  
  // Column positions
  const labelX = margin + 5;
  const valueX = margin + 80;
  
  doc.text('Batches:', labelX, yPos);
  doc.text(`${file.batches.length}`, valueX, yPos);
  yPos += lineHeight + 1;
  
  doc.text('Transactions:', labelX, yPos);
  doc.text(`${totalEntries}`, valueX, yPos);
  yPos += lineHeight + 1;
  
  doc.text('Debit:', labelX, yPos);
  doc.text(`$${totalDebit.toFixed(2)}`, valueX, yPos);
  yPos += lineHeight + 1;
  
  doc.text('Credit:', labelX, yPos);
  doc.text(`$${totalCredit.toFixed(2)}`, valueX, yPos);
  yPos += lineHeight + 2;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Net Amount:', labelX, yPos);
  doc.text(`$${netAmount.toFixed(2)}`, valueX, yPos);
  
  // Footer on every page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    
    const footerY = pageHeight - 10;
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US')}`, margin, footerY);
    doc.text(`NACHA Export`, pageWidth - margin, footerY, { align: 'right' });
  }
  
  // Save PDF
  doc.save('nacha-transactions.pdf');
}

